import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/translations';

import { RouterLinkAs } from '@/lib/router-link';
import {
  AlertDialog, Body, Button, Card, Eyebrow,
  I,
  Label, List, PageHeader, RailRow, Row, SearchInput, Stack, useDelayedLoading,
} from '@leanin/ui';
import { GroupRow } from '../../cards/index';
import type { GroupSummary } from '../../../lib/views/groups';
import { useCircles, useInfiniteNetworkCircles } from '../../../lib/queries/groups';
import {
  useLinkCircleToNetwork,
  useUnlinkCircleFromNetwork,
} from '../../../lib/mutations/groups';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import { useErrorMessage } from '../../../lib/error-message';
import { NetworkCirclesSectionSkeleton } from './NetworkCirclesSection.skeleton';

type Props = {
  readonly networkId: string;
};

const TONE_CYCLE = ['burgundy', 'navy', 'emerald', 'yellow', 'orange'] as const satisfies ReadonlyArray<'burgundy' | 'navy' | 'emerald' | 'yellow' | 'orange'>;

function circleTone(idx: number): 'burgundy' | 'navy' | 'emerald' | 'yellow' | 'orange' {
  return TONE_CYCLE[idx % TONE_CYCLE.length]!;
}

const SEARCH_PAGE_SIZE = 8;

export function NetworkCirclesSection({ networkId }: Props) {
  const tn = useTranslations('Networks');
  const describeError = useErrorMessage();
  // Network admin view surfaces archived circles too — leaders need to see
  // them to manage / unlink. Public-facing surfaces keep the default
  // (`includeArchived: false`).
  const circlesQuery = useInfiniteNetworkCircles(networkId, {
    pageSize: 50,
    includeArchived: true,
  });

  // Auto-fetch remaining pages so the admin always sees — and can unlink — the
  // full set of linked Circles. Networks with >50 Circles would otherwise
  // silently truncate the list, leaving later Circles invisible/un-unlinkable.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = circlesQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const linkMutation = useLinkCircleToNetwork();
  const unlinkMutation = useUnlinkCircleFromNetwork();
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const unlink = useConfirmDialog<{ circleId: string; circleName: string }>();
  const isPending = linkMutation.isPending || unlinkMutation.isPending;

  const trimmedQuery = searchQuery.trim();
  const candidatesQuery = useCircles(
    { q: trimmedQuery, pageSize: SEARCH_PAGE_SIZE },
    { enabled: trimmedQuery.length >= 2 },
  );

  const circles: ReadonlyArray<GroupSummary> = useMemo(
    () => circlesQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [circlesQuery.data],
  );
  const linkedIds = useMemo(() => new Set(circles.map((c) => c.id)), [circles]);
  const candidates = useMemo(() => {
    const items = candidatesQuery.data?.items ?? [];
    // Don't surface circles that are already linked to this network. Circles
    // attached to a DIFFERENT network still appear but flagged — the server
    // refuses the link and we surface that as inline error.
    return items.filter((c) => !linkedIds.has(c.id));
  }, [candidatesQuery.data, linkedIds]);

  const handleSelect = (circle: GroupSummary) => {
    setLinkError(null);
    linkMutation.mutate(
      { networkId, circleId: circle.id },
      {
        // Clearing the search unmounts the result row that held focus, so
        // hand focus back to the search box rather than dropping it to <body>.
        onSuccess: () => {
          setSearchQuery('');
          searchRef.current?.focus();
        },
        onError: (err) =>
          setLinkError(
            tn('circlesAdmin.errors.linkFailedReason', {
              name: circle.name,
              reason: describeError(err),
            }),
          ),
      },
    );
  };

  const handleUnlink = (circleId: string, circleName: string) => {
    unlink.request({ circleId, circleName });
  };

  const confirmUnlink = () => {
    if (unlink.target === null) return;
    unlinkMutation.mutate({ networkId, circleId: unlink.target.circleId });
  };

  const showSkeleton = useDelayedLoading(circlesQuery.data === undefined);
  if (showSkeleton) {
    return <NetworkCirclesSectionSkeleton rows={3} />;
  }
  if (circlesQuery.data === undefined) return null;

  return (
    <Stack gap="lg">
      <PageHeader
        title={tn('circlesAdmin.title')}
        subtitle={tn('circlesAdmin.subtitle')}
      />

      {/* Add an existing Circle (search) OR start a new one (CTA). Both
          paths produce the same end state — a circle linked to this
          network — so they share one card. The CTA navigates to the
          standard Start-a-Circle form with `parentNetworkId` set; the
          server's create-circle handler enforces leadership and persists
          the link in the same transaction as the create. */}
      <Card pad="default">
        <Stack gap="md">
          <Eyebrow tone="accent">{tn('circlesAdmin.addCircle')}</Eyebrow>
          <Row gap="sm" align="center">
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchInput
                ref={searchRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={tn('circlesAdmin.searchPlaceholder')}
                aria-label={tn('circlesAdmin.searchAria')}
              />
            </div>
            <Button
              variant="primary"
              size="md"
              icon={I.plus}
              href={`/circles/new?parentNetworkId=${networkId}`}
              linkAs={RouterLinkAs}
            >
              {tn('circlesAdmin.startCircle')}
            </Button>
          </Row>
          {trimmedQuery.length < 2 ? null : candidatesQuery.isLoading ? (
            <Body size="sm" color="mute">{tn('circlesAdmin.searching')}</Body>
          ) : candidates.length === 0 ? (
            <Body size="sm" color="mute">
              {tn('circlesAdmin.noMatch', { query: trimmedQuery })}
            </Body>
          ) : (
            <Stack gap="none" style={{ maxHeight: 320, overflow: 'auto' }}>
              {candidates.map((circle) => {
                const leader = circle.memberPreview[0];
                const leaderName = leader
                  ? `${leader.firstName} ${leader.lastName}`.trim()
                  : null;
                const memberLabel = tn('circlesAdmin.memberCount', { count: circle.memberCount });
                const alreadyLinked = circle.parentGroupId !== null;
                const baseSubtitle = leaderName !== null
                  ? tn('circlesAdmin.ledBy', { leader: leaderName, members: memberLabel })
                  : memberLabel;
                const subtitle = alreadyLinked
                  ? `${baseSubtitle} · ${tn('circlesAdmin.currentlyInAnotherNetwork')}`
                  : baseSubtitle;
                return (
                  <RailRow
                    key={circle.id}
                    onClick={() => handleSelect(circle)}
                    disabled={isPending}
                    bordered={false}
                    padX="none"
                    title={<Label>{circle.name}</Label>}
                    sub={<Body size="sm" color="mute">{subtitle}</Body>}
                    trailing={
                      <Body size="sm" color="primary" style={{ whiteSpace: 'nowrap' }}>
                        {alreadyLinked ? tn('circlesAdmin.moveHere') : tn('circlesAdmin.add')}
                      </Body>
                    }
                  />
                );
              })}
            </Stack>
          )}
          {linkError !== null ? (
            <Body size="sm" color="destructive">{linkError}</Body>
          ) : null}
        </Stack>
      </Card>

      <Card pad="flush">
        {circles.length === 0 ? (
          <Row p="xl" justify="center">
            <Body size="sm" color="mute">{tn('circlesAdmin.empty')}</Body>
          </Row>
        ) : (
          <List bordered={false}>
            {circles.map((circle, idx) => {
              const tone = circleTone(idx);
              const leader = circle.memberPreview[0];
              const leaders = leader !== undefined
                ? [{
                    name: `${leader.firstName} ${leader.lastName}`,
                    ...(leader.photoUrl !== null ? { photo: leader.photoUrl } : {}),
                  }]
                : [];
              return (
                <GroupRow
                  key={circle.id}
                  kind="circle"
                  href={`/circles/${circle.id}`}
                  ariaLabel={tn('circlesAdmin.openAria', { name: circle.name })}
                  linkAs={RouterLinkAs}
                  name={circle.name}
                  tone={tone}
                  memberCount={circle.memberCount}
                  closed={circle.access !== 'public'}
                  archived={circle.status === 'archived'}
                  leaders={leaders}
                  trailing={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnlink(circle.id, circle.name)}
                      disabled={isPending}
                    >
                      {tn('circlesAdmin.remove')}
                    </Button>
                  }
                />
              );
            })}
          </List>
        )}
      </Card>

      <AlertDialog
        open={unlink.open}
        onOpenChange={unlink.onOpenChange}
        title={unlink.target !== null ? tn('circlesAdmin.removeDialog.title', { name: unlink.target.circleName }) : tn('circlesAdmin.removeDialog.titleFallback')}
        description={tn('circlesAdmin.removeDialog.description')}
        actionLabel={tn('circlesAdmin.removeDialog.action')}
        cancelLabel={tn('circlesAdmin.removeDialog.cancel')}
        destructive
        onAction={confirmUnlink}
      />
    </Stack>
  );
}
